/**
 * Reload the web app when the shell is reopened after a long time in the
 * background, so a resumed session shows fresh content instead of a stale page
 * (the remote web app is otherwise only loaded on a cold start).
 *
 * Only reloads after a meaningful absence, so quick app-switches never reload
 * and can't interrupt the user. No-op outside the Capacitor shell; never throws.
 */

import { App } from "@capacitor/app";
import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";
import { reloadWebApp } from "./reload_page";

// Reload only if the app was backgrounded at least this long. Short switches
// (checking a notification, copying a code) stay well under this and are safe.
const STALE_AFTER_MS = 30 * 60 * 1000; // 30 minutes

let backgroundedAt = null;
let registered = false;

function reportResumeError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_resume_refresh" },
    });
  } catch {
    // reporting must never break the app
  }
}

function handleStateChange({ isActive }) {
  if (!isActive) {
    backgroundedAt = Date.now();
    return;
  }
  // Becoming active: reload only after a long absence.
  if (backgroundedAt != null && Date.now() - backgroundedAt > STALE_AFTER_MS) {
    backgroundedAt = null;
    reloadWebApp();
  }
}

/**
 * Start refreshing the web app on resume-after-long-background. No-op off-shell;
 * runs once; never throws.
 */
export function registerResumeRefresh() {
  if (!isCapacitorEnvironment() || registered) {
    return;
  }
  registered = true;
  try {
    const result = App.addListener("appStateChange", handleStateChange);
    if (result && typeof result.catch === "function") {
      result.catch(reportResumeError);
    }
  } catch (error) {
    registered = false; // allow a later call to retry
    reportResumeError(error);
  }
}
